import * as React from 'react';
import { useState } from 'react';
import { Icon, Checkbox, Tooltip, message } from 'antd';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import { bindActionCreators, ActionCreator } from 'redux';
import { changeSelection, getAllInquiriesApplyFilter } from 'src/actions/inquiryShipperActions';
import { StylesProps } from 'src/Library/Types';
import SendIcon from 'src/components/common/icons/send';
import ExtendBulkDeadlineModal from '../Modals/ExtendBulkDeadlineModal';
import { BULK_INQUIRY_COLORS } from 'src/components/pages/Enquiry/InquiryUtils';
import SendBulkInquiryModal from '../Modals/SendBulkInquiryModal';
import { InquiryModes } from 'src/Library/Constants';
import moment from 'moment';
import BroadcastMessage from '../Modals/BroadcastMessageModal';
import { tokens } from 'src/design-system';

const styles = (theme: any) => ({
  parent: {
    padding: '5px 20px 5px 20px',
    fontSize: `${tokens.fontSize.body}px`,
    fontWeight: tokens.fontWeight.semibold,
    width: 'calc( 100% + 10px )',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    transform: 'translateX( -5px )',
    backgroundColor: 'white',
    zIndex: '2',
    position: 'sticky',
    top: 0,
    marginTop: '10px',
    visibility: 'hidden',
  },
  actions: {
    fontSize: `${tokens.fontSize.body}px`,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.4s',
  },
  actionItems: {
    transition: 'all 0.4s',
  },
  iconsTheme: {
    flex: 1,
    marginBottom: '8px',
    '&:hover': {
      color: BULK_INQUIRY_COLORS.GREEN,
    },
  },
});

interface ReduxProps {
  selectedItems: Record<string, any>;
  data: Record<string, any>;
  changeSelection: (items: Record<string, any>) => ActionCreator<void>;
  permissions: Record<string, boolean>;
}

interface BulkActionProps extends StylesProps<ReturnType<typeof styles>>, ReduxProps {
  getAllInquiriesApplyFilter: () => ActionCreator<void>;
}

const UpdateBulkInquiry = (props: BulkActionProps) => {
  const { classes, changeSelection, selectedItems, data, permissions } = props;
  const { INQUIRY_CREATE_UPDATE_ACTIONS, INQUIRY_SEND_ACTIONS, INQUIRY_VIEW_BIDS_ACTIONS } = permissions;
  const [isBulkDeadlineModalVisible, setIsBulkDeadlineModalVisible] = useState(false);
  const [isSendBulkInquiryModalVisible, setIsSendBulkInquiryModalVisible] = useState(false);
  const [isBroadcastMessageModalVisible, setIsBroadcastMessageModalVisible] = useState(false);

  const isExtendDeadlineDisabled = Object.values(selectedItems).some((inquiry) =>
    ['CONFIRMED'].includes(inquiry?.status)
  );
  const isSendInquiryDisabled = Object.values(selectedItems).some((inquiry) =>
    ['CONFIRMED', 'SENT', 'CLOSED'].includes(inquiry?.status)
  );
  const isBroadcastMessageDisabled = Object.values(selectedItems).some((inquiry) => {
    if (['OPEN'].includes(inquiry?.status)) {
      return !inquiry?.totalBids;
    }
    return true;
  });

  const selectedCount = Object.keys(selectedItems).length;
  const totalCount = Object.keys(data).length;

  const styleBroadcastMessage = isBroadcastMessageDisabled || !INQUIRY_VIEW_BIDS_ACTIONS
    ? {
        opacity: 0.2,
        cursor: 'not-allowed',
      }
    : { cursor: 'pointer' };

  const styleExtendDeadline = isExtendDeadlineDisabled || !INQUIRY_CREATE_UPDATE_ACTIONS
    ? {
        color: BULK_INQUIRY_COLORS.GREY,
      }
    : { cursor: 'pointer' };

  const styleSendInquiries = isSendInquiryDisabled || !INQUIRY_SEND_ACTIONS
    ? {
        fill: BULK_INQUIRY_COLORS.GREY,
      }
    : { fill: 'black' };

  function handleSelectAll() {
    let selectionObject = {};
    data.forEach((inquiryData) => {
      selectionObject[inquiryData.id] = inquiryData;
    });
    if (selectedCount === totalCount) selectionObject = {};
    changeSelection(selectionObject);
  }

  const renderSelectAll = () => {
    return (
      <span style={{ marginRight: '10px' }} onClick={() => handleSelectAll()}>
        <Checkbox
          style={{ marginRight: '10px' }}
          indeterminate={selectedCount !== totalCount}
          checked={selectedCount === totalCount}
        />
        <span className={classes.actionItems}> Select All </span>
      </span>
    );
  };

  const renderCount = () => {
    return <span>{selectedCount} {selectedCount > 1 ? 'items' : 'item'} selected</span>;
  };

  const isValidInquiryMode = () => {
    let isForwarderPresent = false,
      isTruckingAgetPresent = false;
    Object.values(selectedItems).forEach((inquiry) => {
      if (inquiry.mode === InquiryModes.TRAILER || inquiry.mode === InquiryModes.DOMESTIC) {
        isTruckingAgetPresent = true;
      } else {
        isForwarderPresent = true;
      }
    });
    return !(isForwarderPresent && isTruckingAgetPresent);
  };

  const isValidInquiryStatus = () => {
    let isValidStatus = true;

    Object.values(selectedItems).forEach((inquiry) => {
      const deadline = moment(inquiry?.deadline);
      if (
        inquiry.status === 'CONFIRMED' ||
        inquiry.status === 'SENT' ||
        inquiry.status === 'CLOSED' ||
        (inquiry.status === 'OPEN' && deadline.isBefore(moment()))
      ) {
        isValidStatus = false;
      }
    });
    return isValidStatus;
  };

  const SendBulkInquiry = () => {
    if (!isValidInquiryMode()) {
      message.error('Selected Inquiries have different Inquiry Modes');
    } else if (!isValidInquiryStatus()) {
      message.error('Deadline has passed for some of the selected Inquiries');
    } else {
      setIsSendBulkInquiryModalVisible(true);
    }
  };

  const handleBroadcastMessageModal = (flag: boolean, isReload = false) => {
    if (isReload) {
      changeSelection({});
      props.getAllInquiriesApplyFilter();
    }
    setIsBroadcastMessageModalVisible(flag);
  };

  const renderSendIcon = () => {
    return (
      <SendIcon
        style={{
          width: '18px',
          height: '18px',
          transform: 'scale(0.9)',
          marginLeft: '0px',
          cursor: 'pointer',
          ...styleSendInquiries,
        }}
        onClick={() => (isSendInquiryDisabled || !INQUIRY_SEND_ACTIONS ? undefined : SendBulkInquiry())}
      />
    );
  };

  const renderBulkInquiryDeadlineIcon = () => {
    return (
      <div style={{ marginLeft: '20px' }}>
        <Tooltip title={isExtendDeadlineDisabled || !INQUIRY_CREATE_UPDATE_ACTIONS ? !INQUIRY_CREATE_UPDATE_ACTIONS ? 'Permission Required' : '' : 'Extend Deadline'} placement="left">
          <Icon
            type="calendar"
            className={classes.iconsTheme}
            style={styleExtendDeadline}
            onClick={() =>
              isExtendDeadlineDisabled || !INQUIRY_CREATE_UPDATE_ACTIONS ? undefined : setIsBulkDeadlineModalVisible(true)
            }
          />
        </Tooltip>
      </div>
    );
  };

  const renderSendBulkInquiryIcon = () => {
    return !isSendBulkInquiryModalVisible ? (
      <div style={{ marginLeft: '20px' }}>
        <Tooltip title={isSendInquiryDisabled || !INQUIRY_SEND_ACTIONS ? !INQUIRY_SEND_ACTIONS ? 'Permission Required' : '' : 'Send Inquiry'} placement="left">
          {renderSendIcon()}
        </Tooltip>
      </div>
    ) : (
      <div style={{ marginLeft: '20px' }}>{renderSendIcon()}</div>
    );
  };

  const renderBroadcaseMessageIcon = () => {
    return (
      <div style={{ marginLeft: '20px' }}>
        <Tooltip title={isBroadcastMessageDisabled || !INQUIRY_VIEW_BIDS_ACTIONS ? !INQUIRY_VIEW_BIDS_ACTIONS ? 'Permission Required' : '' : 'Broadcast Message'} placement="left">
          <img
            style={{
              marginTop: 4,
              ...styleBroadcastMessage,
            }}
            className={classes.iconsTheme}
            width="14px"
            height="14px"
            src={require('src/assets/images/broadcast_icon.svg')}
            alt="Boadcast Message"
            onClick={() =>
              isBroadcastMessageDisabled || !INQUIRY_VIEW_BIDS_ACTIONS ? undefined : handleBroadcastMessageModal(true)
            }
          />
          {/* <Icon
            type="message"
            className={classes.iconsTheme}
            style={isBroadcastMessageDisabled ? { color: BULK_INQUIRY_COLORS.GREY } : {}}
            onClick={() =>
              isBroadcastMessageDisabled ? undefined : setIsBroadcastMessageModalVisible(true)
            }
          /> */}
        </Tooltip>
      </div>
    );
  };

  const renderActions = () => {
    return (
      <span className={classes.actions}>
        {renderCount()}
        {renderBulkInquiryDeadlineIcon()}
        {renderSendBulkInquiryIcon()}
        {renderBroadcaseMessageIcon()}
      </span>
    );
  };

  const renderExtendBulkDeadlineModal = () => {
    return (
      <ExtendBulkDeadlineModal
        isVisible={isBulkDeadlineModalVisible}
        onClose={({ isSuccess, closeModal = true }) => {
          if (closeModal) {
            setIsBulkDeadlineModalVisible(false);
          }
        }}
        onSubmit={handleBulkDeadlineExtension}
      />
    );
  };

  const renderSendBulkInquiryModal = () => {
    return (
      <SendBulkInquiryModal
        isVisible={isSendBulkInquiryModalVisible}
        onClose={({ isSuccess, closeModal = true }) => {
          if (closeModal) {
            setIsSendBulkInquiryModalVisible(false);
          }
        }}
        onSubmit={handleSendBulkInquiry}
      />
    );
  };

  const renderBroadCastMessageModal = () => {
    return (
      <BroadcastMessage
        isVisible={isBroadcastMessageModalVisible}
        onClose={(flag) => handleBroadcastMessageModal(false, flag)}
        selectedInquiry={selectedItems}
      />
    );
  };

  const handleBulkDeadlineExtension = (date) => {
    props.getAllInquiriesApplyFilter();
    setIsBulkDeadlineModalVisible(false);
    changeSelection({});
  };

  const handleSendBulkInquiry = () => {
    props.getAllInquiriesApplyFilter();
    setIsSendBulkInquiryModalVisible(false);
    changeSelection({});
  };

  const renderBox = () => {
    return (
      <div className={classes.parent} style={selectedCount > 0 ? { visibility: 'visible' } : {}}>
        {renderSelectAll()}
        {renderActions()}
        {isBulkDeadlineModalVisible && renderExtendBulkDeadlineModal()}
        {isSendBulkInquiryModalVisible && renderSendBulkInquiryModal()}
        {isBroadcastMessageModalVisible && renderBroadCastMessageModal()}
      </div>
    );
  };

  return renderBox();
};

const mapStateToProps = ({ inquiryShipperReducer, masterData }) => {
  return {
    selectedItems: inquiryShipperReducer.selectedItems || {},
    data: inquiryShipperReducer.allInquiries,
    permissions: masterData.permissions,
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getAllInquiriesApplyFilter,
      changeSelection,
    },
    dispatch
  );

export default injectSheet(styles)(connect(mapStateToProps, mapDispatchToProps)(UpdateBulkInquiry));
